export const mapStatusToDisplay = (dbStatus) => {
  const statusMap = {
    pending: 'Draft',
    active: 'Active',
    complete: 'Complete',
    inactive: 'Inactive',
  };
  return statusMap[String(dbStatus || '').toLowerCase()] || dbStatus;
};

export const mapDisplayToDbStatus = (displayStatus) => {
  const statusMap = {
    Draft: 'pending',
    'On Track': 'pending',
    Pending: 'pending',
    Active: 'active',
    Ongoing: 'active',
    Complete: 'complete',
    Completed: 'complete',
    Inactive: 'inactive',
  };
  return statusMap[displayStatus] || String(displayStatus || '').toLowerCase();
};

export const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
};

export const isOverdue = (project) => {
  if (String(project.status).toLowerCase() === 'complete') return false;
  const deadline = formatDate(project.deadLine);
  if (!deadline) return false;
  return deadline < new Date().toISOString().split('T')[0];
};

export const getInitials = (firstName, lastName) => {
  const a = String(firstName || '').trim().charAt(0);
  const b = String(lastName || '').trim().charAt(0);
  return (a + b).toUpperCase() || '?';
};

export const computeProgress = (project) => {
  const services = resolveProjectServices(project);
  const tasks = Array.isArray(project.tasks) ? project.tasks : [];
  const totalServices = services.length;
  const db = String(project.status || '').toLowerCase();
  const isComplete = db === 'complete';

  const isTaskDone = (status) => {
    const s = String(status || '')
      .toLowerCase()
      .trim();
    return s === 'approve' || s === 'approved' || s === 'complete' || s === 'completed';
  };

  // Prefer real task completion when tasks exist.
  if (tasks.length > 0) {
    const done = tasks.filter((t) => isTaskDone(t.status)).length;
    return {
      percent: isComplete ? 100 : Math.round((done / tasks.length) * 100),
      done: isComplete ? tasks.length : done,
      total: tasks.length,
      unit: 'tasks',
    };
  }

  // No tasks: do not invent progress. Ongoing/pending = 0%; complete = 100%.
  if (isComplete) {
    const total = totalServices || 1;
    return {
      percent: 100,
      done: total,
      total,
      unit: totalServices > 0 ? 'services' : 'tasks',
    };
  }

  if (totalServices > 0) {
    return {
      percent: 0,
      done: 0,
      total: totalServices,
      unit: 'services',
    };
  }

  return { percent: 0, done: 0, total: 1, unit: 'tasks' };
};

/** Weekly / fortnightly / monthly series (not one-off). */
export const isRecurringProjectFrequency = (freq) => {
  const raw = String(freq || '').trim().toLowerCase();
  if (!raw) return false;
  const hay = raw.includes('|') ? raw.split('|').join(' ') : raw;
  if (/one[\s-]?time|one[\s-]?off|\bonce\b|\bsingle\b/.test(hay)) return false;
  return (
    [
      'daily',
      'weekly',
      'fortnightly',
      'monthly',
      'quarterly',
      'yearly',
    ].includes(hay) ||
    /\bdaily\b|\bweekly\b|\bfortnight|\bmonthly\b|\bquarter|\byearly\b|\bbi-?annual/.test(
      hay,
    )
  );
};

/** Capitalize recurrenceFrequency for display (daily → Daily). */
export const formatRecurrenceFrequencyLabel = (freq) => {
  const raw = String(freq ?? '').trim();
  if (!raw) return null;
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
};

/**
 * Prefer this project's visit cadence, then selected-service snapshot.
 * Do not inherit leftover booking / lead frequencies.
 */
export const resolveProjectFrequency = (
  project,
  bookingFrequency,
  serviceFrequencies = [],
  stageName = '',
) => {
  const fromProject = formatRecurrenceFrequencyLabel(project?.recurrenceFrequency);
  if (fromProject) return fromProject;

  const projectSvcs = Array.isArray(project?.projectServices)
    ? project.projectServices
    : [];
  if (projectSvcs.length > 0) {
    const freqs = [
      ...new Set(
        projectSvcs
          .map((s) => String(s?.frequency || '').trim())
          .filter(Boolean),
      ),
    ];
    if (freqs.length > 0) return freqs.join(', ');
  }

  return resolveBookingFrequency(bookingFrequency, serviceFrequencies, stageName);
};

export const computeNextService = (project) => {
  if (project?.recurrenceActive && project?.nextVisitAt) {
    const nextVisit = formatDate(project.nextVisitAt);
    if (nextVisit) return nextVisit;
  }

  const pendingTask = (project.tasks || [])
    .filter((t) => String(t.status).toLowerCase() !== 'approve')
    .map((t) => formatDate(t.date))
    .filter(Boolean)
    .sort()[0];
  if (pendingTask) return pendingTask;

  const start = formatDate(project.startDate);
  const deadline = formatDate(project.deadLine);
  const today = new Date().toISOString().split('T')[0];

  if (start && start >= today) return start;
  if (deadline && deadline >= today) return deadline;
  if (start) return start;
  if (deadline) return deadline;
  return 'Not scheduled';
};

export const collectTeamMembers = (project, { limit = 4, includeManager = true } = {}) => {
  const members = [];
  const seen = new Set();

  const add = (user) => {
    if (!user?.id || seen.has(user.id)) return;
    seen.add(user.id);
    members.push({
      id: user.id,
      initials: getInitials(user.firstName, user.lastName),
      name: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
    });
  };

  if (project.manager) {
    if (includeManager) add(project.manager);
    else seen.add(project.manager.id);
  }
  // Rostered cleaners are the task's assignees; `user` is its creator/owner.
  (project.tasks || []).forEach((t) => {
    (Array.isArray(t.assignees) ? t.assignees : []).forEach(add);
    add(t.user);
  });
  return members.slice(0, limit);
};

export const formatMoney = (value) => {
  const n = Number(String(value || '').replace(/[^0-9.-]/g, ''));
  if (!Number.isFinite(n) || n === 0) {
    const raw = String(value || '').trim();
    return raw || '—';
  }
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  }).format(n);
};

export const statusDetailClass = (status) => {
  const key = String(status).toLowerCase().replace(/\s+/g, '-');
  return `pd-status pd-status--${key}`;
};

const EMAIL_ONLY = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Convert contract rich-text HTML into a readable project description. */
export const htmlToPlainText = (value) => {
  const raw = String(value ?? '');
  if (!raw.trim()) return '';
  if (!/<[a-z][\s\S]*>/i.test(raw)) return raw.trim();
  return raw
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\s*\/p\s*>/gi, '\n')
    .replace(/<\s*\/div\s*>/gi, '\n')
    .replace(/<\s*\/h[1-6]\s*>/gi, '\n')
    .replace(/<\s*\/tr\s*>/gi, '\n')
    .replace(/<\s*li[^>]*>/gi, '\n- ')
    .replace(/<\s*\/?(ul|ol)[^>]*>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&apos;/gi, "'")
    .replace(/\u00a0/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

/** Ignore empty, email-only, or contact-field values mistakenly stored as description. */
export const resolveProjectDescription = (message, contactEmail = '') => {
  const text = htmlToPlainText(message);
  if (!text) return null;
  const email = String(contactEmail ?? '').trim().toLowerCase();
  if (email && text.toLowerCase() === email) return null;
  if (EMAIL_ONLY.test(text)) return null;
  return text;
};

/** Avoid showing pipeline stage names as service frequency. */
export const resolveBookingFrequency = (
  bookingFrequency,
  serviceFrequencies = [],
  stageName = '',
) => {
  const fromServices = [...new Set(serviceFrequencies.map((f) => String(f || '').trim()).filter(Boolean))];
  if (fromServices.length > 0) return fromServices.join(', ');

  const raw = String(bookingFrequency ?? '').trim();
  if (!raw) return null;
  const stage = String(stageName ?? '').trim();
  if (stage && raw.toLowerCase() === stage.toLowerCase()) return null;

  const lower = raw.toLowerCase();
  const looksLikeStage =
    /opportunity|assessment|assesment|proposal|negotiation|closed|won|lost|lead|qualified/i.test(
      lower,
    );
  if (looksLikeStage && !/weekly|fortnight|monthly|daily|once|one.?off|annual|yearly/i.test(lower)) {
    return null;
  }

  return raw;
};

/** Avoid "sales pipeline pipeline" when the stored name already ends with "pipeline". */
export const formatPipelineLabel = (name) => {
  const trimmed = String(name ?? '').trim();
  if (!trimmed) return '';
  return /pipeline$/i.test(trimmed) ? trimmed : `${trimmed} pipeline`;
};

/** Prefer services chosen on this project; then quote; then booking. */
export const resolveProjectServices = (project) => {
  const selected = project?.projectServices;
  if (Array.isArray(selected) && selected.length > 0) {
    return selected;
  }
  const quoted = project?.quotedServices;
  if (Array.isArray(quoted) && quoted.length > 0) {
    return quoted;
  }
  return project?.booking?.bookingServices || [];
};

export const resolveProjectServiceNames = (project) =>
  resolveProjectServices(project)
    .map((s) => s?.service?.service)
    .filter(Boolean);

export const resolveProjectServiceLabel = (project) => {
  const names = resolveProjectServiceNames(project);
  if (names.length === 1) return names[0];
  if (names.length > 1) return `${names.length} services`;
  return '—';
};

export const resolveProjectQuotedTotal = (project) => {
  const total = project?.quotedTotal;
  if (total != null && Number.isFinite(Number(total))) {
    return Number(total);
  }
  return null;
};

/**
 * Sum of selected project service line prices (ex-GST), matching Add Project form.
 * Prefer prices embedded on projectServices; else match booking.bookingServices.
 */
export const resolveProjectSelectedServicesTotal = (project) => {
  const selected = Array.isArray(project?.projectServices)
    ? project.projectServices
    : [];
  const bookingRows = project?.booking?.bookingServices || [];
  const priceByServiceId = new Map();
  for (const row of bookingRows) {
    const sid = String(row?.service?.id || '').trim();
    if (!sid) continue;
    const amount = Number(row.total ?? row.price);
    if (Number.isFinite(amount) && amount > 0) {
      priceByServiceId.set(sid, amount);
    }
  }

  const rows = selected.length > 0 ? selected : bookingRows;
  if (!rows.length) return null;

  let sum = 0;
  let counted = 0;
  for (const row of rows) {
    const sid = String(row?.service?.id || '').trim();
    const embedded = Number(row?.total ?? row?.price);
    const fromBooking = sid ? priceByServiceId.get(sid) : undefined;
    const amount =
      Number.isFinite(embedded) && embedded > 0
        ? embedded
        : fromBooking != null
          ? fromBooking
          : NaN;
    if (!Number.isFinite(amount) || amount <= 0) continue;
    sum += amount;
    counted += 1;
  }
  if (counted === 0) return null;
  return sum;
};

/** Header amount: selected services → accepted quote → booking estimate. */
export const resolveProjectDisplayTotal = (project) => {
  const selected = resolveProjectSelectedServicesTotal(project);
  if (selected != null) return selected;
  const quoted = resolveProjectQuotedTotal(project);
  if (quoted != null) return quoted;
  const estimate = Number(project?.booking?.estimatedPRice);
  if (Number.isFinite(estimate) && estimate > 0) return estimate;
  return null;
};
